import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalization } from '../../contexts/LocalizationContext'
import { SUPPORT_EMAIL } from '../../constants/contact'

interface HeaderProps {
  showToggleButton?: boolean
  onToggleView?: () => void
  showList?: boolean
  stepIndicator?: string
  onEditLicensePlate?: () => void
  showHamburgerMenu?: boolean
  parkingInfo?: { plate?: string; city?: string }
}

export function Header({ showToggleButton = false, onToggleView, showList, stepIndicator, onEditLicensePlate, showHamburgerMenu = true, parkingInfo }: HeaderProps) {
  const { signOut } = useAuth()
  const { t } = useLocalization()
  const location = useLocation()
  const persistedMode = sessionStorage.getItem('preferred_subscription_mode') || localStorage.getItem('preferred_subscription_mode')
  const subscriptionLink = persistedMode === 'direct' || persistedMode === 'direct2'
    ? `/subscription?sub=${persistedMode}`
    : '/subscription'
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Hide logo on mobile for subscription pages
  const isSubscriptionPage = location.pathname.includes('subscription')
  const logoClasses = `text-xl font-bold text-paybyphone-green hover:text-paybyphone-green/80 transition-colors ${
    isSubscriptionPage ? 'hidden md:block' : ''
  }`

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      setShowMenu(false) // Close menu after sign out
    } catch (error) {
      console.error('Error signing out:', error)
      // Force local logout even if server logout fails
      setShowMenu(false)
      // Clear local storage and reload page as fallback
      localStorage.clear()
      sessionStorage.clear()
      window.location.reload()
    }
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between relative z-50">
      <div className="flex items-center">
        {/* Parking Icon for mobile, Logo for desktop */}
        <Link to="/dashboard" className="flex items-center">
          <div className="md:hidden w-8 h-8 bg-paybyphone-green rounded-lg flex items-center justify-center mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="2.5" fill="white"/>
            </svg>
          </div>
          <span className={`${logoClasses} hidden md:block`}>Parks Avenue</span>
        </Link>
        {parkingInfo && (parkingInfo.plate || parkingInfo.city) && (
          <div className="flex items-center gap-2 ml-3 text-sm">
            {parkingInfo.plate && (
              <span className="bg-slate-800 text-white rounded px-2 py-0.5 font-bold tracking-wider text-xs">{parkingInfo.plate}</span>
            )}
            {parkingInfo.city && (
              <span className="text-slate-500 font-medium text-xs sm:text-sm">{parkingInfo.city}</span>
            )}
          </div>
        )}
      </div>

      {/* Step Indicator */}
      {stepIndicator && (
        <div className="flex-1 text-center">
          <span className="text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {stepIndicator}
          </span>
        </div>
      )}

      <div className="flex items-center space-x-3">
        {/* Toggle view button for mobile (only on Dashboard) */}
        {showToggleButton && onToggleView && (
          <button
            onClick={onToggleView}
            className="md:hidden btn-secondary text-sm px-3 py-2"
          >
            {showList ? t('navigation.map') : t('navigation.list')}
          </button>
        )}
        
        {/* Hamburger Menu */}
        {showHamburgerMenu && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="text-gray-600 hover:text-gray-800 p-2"
              title="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showMenu && (
              <div className="header-dropdown-menu absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                <Link
                  to="/dashboard"
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setShowMenu(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
                  </svg>
                  {t('navigation.dashboard')}
                </Link>
                <Link
                  to={subscriptionLink}
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setShowMenu(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  {t('navigation.subscription')}
                </Link>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setShowMenu(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('navigation.help')}
                </a>
                {onEditLicensePlate && (
                  <button
                    onClick={() => {
                      onEditLicensePlate()
                      setShowMenu(false)
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 .552-.224 1.052-.586 1.414A1.992 1.992 0 0110 13H7a2 2 0 01-2-2V7a2 2 0 012-2h3a2 2 0 012 2v4zm0 0v6m0 0h4m-4 0H8" />
                    </svg>
                    {t('navigation.editLicensePlate')}
                  </button>
                )}
                <div className="border-t border-gray-200 my-2"></div>
                <Link
                  to="/terms"
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setShowMenu(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('navigation.terms')}
                </Link>
                <Link
                  to="/privacy"
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                  onClick={() => setShowMenu(false)}
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  {t('navigation.privacy')}
                </Link>
                <div className="border-t border-gray-200 my-2"></div>
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1" />
                  </svg>
                  {t('navigation.signOut')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
